/**
 * Les choix par défaut de la page « Stages disponibles ».
 *
 * Un parent arrivait sur trois listes vides — enfant, centre, période — et ne
 * voyait rien tant qu'il ne les avait pas toutes remplies, centre par centre.
 * La page part désormais de ce qu'on sait déjà : ses enfants, leur école, et
 * la prochaine période de stages.
 */

/** Sans accents, sans ponctuation, en minuscules : « Sainte-Alène » → « sainte alene ». */
export function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Le centre présélectionné pour les enfants cochés : celui de leur école
 * s'ils vont tous dans la même, sinon tous les centres (chaîne vide).
 *
 * Le lien est `centers.school_id` (depuis la migration des journées
 * pédagogiques, 21/10/2026) : la comparaison des noms qui le remplaçait
 * (« Forest - Sainte-Alène » cherché dans le nom de l'école) n'a plus lieu
 * d'être. `kids.ecole` porte l'identifiant d'une école de la liste, ou un nom
 * tapé à la main, qui ne désigne aucun centre. Deux centres pour une même
 * école : on ne devine pas.
 */
export function centreParDefaut(
  enfants: readonly { ecole: string | null }[],
  centres: readonly { id: string; school_id?: string | null }[],
): string {
  if (enfants.length === 0) return '';

  const parEnfant = enfants.map((k) => {
    const ecole = (k.ecole ?? '').trim();
    if (!ecole) return null;
    const relies = centres.filter((c) => c.school_id === ecole);
    return relies.length === 1 ? relies[0].id : null;
  });

  const premier = parEnfant[0];
  return premier && parEnfant.every((c) => c === premier) ? premier : '';
}

/**
 * Les périodes qui ont encore des sessions à venir, de la plus proche à la
 * plus lointaine. La première est celle qu'on présélectionne.
 */
export function periodesAVenir(
  sessions: readonly { periode: string | null; start_date: string; end_date: string }[],
  aujourdhui: string = new Date().toISOString().slice(0, 10),
): string[] {
  const premiereDate = new Map<string, string>();
  for (const s of sessions) {
    if (!s.periode || s.end_date < aujourdhui) continue;
    const deja = premiereDate.get(s.periode);
    if (!deja || s.start_date < deja) premiereDate.set(s.periode, s.start_date);
  }
  return [...premiereDate.entries()]
    .sort(([, a], [, b]) => a.localeCompare(b))
    .map(([periode]) => periode);
}

/**
 * L'enfant à proposer après « Ajouter au panier », quand le compte en a
 * plusieurs (VALIDÉ ÉCLOSION, 30/10/2026) : le suivant dans la liste après
 * celui qu'on vient d'inscrire, en revenant au début, sans ceux déjà faits
 * pendant cette visite ni ceux qui ne peuvent pas s'inscrire. `null` : il n'y
 * en a plus, on va au panier.
 */
export function enfantSuivant<K extends { id: string }>(
  enfants: readonly K[],
  courantId: string | null,
  faits: ReadonlySet<string>,
  ecarte: (k: K) => boolean = () => false,
): K | null {
  const depart = Math.max(0, enfants.findIndex((k) => k.id === courantId));
  for (let i = 1; i <= enfants.length; i++) {
    const k = enfants[(depart + i) % enfants.length];
    if (k.id !== courantId && !faits.has(k.id) && !ecarte(k)) return k;
  }
  return null;
}
